import React, { useState, useEffect, useCallback } from "react";
import { toast } from "react-toastify";
import { Ban } from "lucide-react";
import axiosInstance from "../../axiosInstance";
import GeneralTable from "../../components/Table/GeneralTable";
import StatusCard from "../../components/StatusCard/StatusCard";
import PageHeader from "../../components/ui/PageHeader";
import SummaryCard from "../../components/ui/SummaryCard";
import { normalizeStatus } from "../../components/ui/StatusBadge";
import ConfirmationDialog from "../../components/ui/ConfirmationDialog";
import { ErrorState } from "../../components/ui/Feedback";
import "./EmergencyFunds.scss";

const API = `${process.env.REACT_APP_API_EMPLOYEE_URL}/emergency-fund`;

const EMPTY_REQUEST = {
  fundType: "LOAN",
  totalAmount: "",
  noOfInstallments: "",
  installmentAmount: 0,
};

const inr = (value) => `₹${(value || 0).toLocaleString("en-IN")}`;

function EmergencyFunds() {
  const [fundRequests, setFundRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState(null);
  const [newRequest, setNewRequest] = useState(EMPTY_REQUEST);
  const [pendingCancelId, setPendingCancelId] = useState(null);
  const [cancelling, setCancelling] = useState(false);

  const fetchFundRequests = useCallback(async () => {
    setLoadError(false);
    try {
      const response = await axiosInstance.get(`${API}/all`);
      setFundRequests(response.data.data || []);
    } catch (err) {
      console.error("Error fetching fund requests:", err);
      setLoadError(true);
    }
  }, []);

  useEffect(() => {
    setLoading(true);
    fetchFundRequests().finally(() => setLoading(false));
  }, [fetchFundRequests]);

  const retryLoad = () => {
    setLoading(true);
    fetchFundRequests().finally(() => setLoading(false));
  };

  // Raw input strings are stored; the installment amount is recomputed from
  // the parsed values on every change.
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormError(null);

    setNewRequest((prev) => {
      const updated = { ...prev, [name]: value };
      const total = parseFloat(updated.totalAmount);
      const installments = parseInt(updated.noOfInstallments, 10);

      updated.installmentAmount =
        total > 0 && installments > 0 ? (total / installments).toFixed(2) : 0;
      return updated;
    });
  };

  const handleSubmitRequest = async () => {
    const totalAmountNum = parseFloat(newRequest.totalAmount);
    const noOfInstallmentsNum = parseInt(newRequest.noOfInstallments, 10);

    if (
      !newRequest.fundType ||
      isNaN(totalAmountNum) ||
      totalAmountNum <= 0 ||
      isNaN(noOfInstallmentsNum) ||
      noOfInstallmentsNum < 1
    ) {
      toast.error("Please provide a valid amount and number of installments.");
      return;
    }
    if (submitting) return;

    setSubmitting(true);
    setFormError(null);
    try {
      await axiosInstance.post(`${API}/create`, {
        fundType: newRequest.fundType,
        totalAmount: totalAmountNum,
        noOfInstallments: noOfInstallmentsNum,
      });

      setNewRequest(EMPTY_REQUEST);
      toast.success("Request submitted successfully!");
      fetchFundRequests();
    } catch (err) {
      console.error("Error submitting fund request:", err);
      toast.error("Failed to submit request. Please try again.");
      setFormError("Failed to submit request. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancelConfirmed = async () => {
    if (!pendingCancelId) return;
    setCancelling(true);
    try {
      await axiosInstance.put(`${API}/cancel/${pendingCancelId}`);
      toast.success("Fund request cancelled successfully.");
      fetchFundRequests();
    } catch (err) {
      console.error("Error cancelling request:", err);
      toast.error(
        `Error: ${
          err.response?.data?.message ||
          err.message ||
          "An unexpected error occurred"
        }`,
      );
    } finally {
      setCancelling(false);
      setPendingCancelId(null);
    }
  };

  // ---- statistics -------------------------------------------------------------
  const stats = fundRequests.reduce(
    (acc, req) => {
      const { key } = normalizeStatus(req.fundApprovalStatus);
      if (key === "approved") acc.approved += 1;
      else if (key === "pending") acc.pending += 1;
      else if (key === "rejected" || key === "cancelled") acc.rejected += 1;
      acc.totalAmount += req.totalAmount || 0;
      return acc;
    },
    { approved: 0, pending: 0, rejected: 0, totalAmount: 0 },
  );

  // ---- history table ----------------------------------------------------------
  const rows = fundRequests.map((req) => ({
    ...req,
    formattedTotalAmount: inr(req.totalAmount),
    formattedInstallmentAmount: inr(req.installmentAmount),
    formattedPendingAmount: inr(req.pendingAmount),
    formattedDate: req.createdAt
      ? new Date(req.createdAt).toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        })
      : "N/A",
  }));

  const columns = [
    {
      header: "Total Amount",
      accessor: "formattedTotalAmount",
      sortAccessor: (r) => r.totalAmount || 0,
    },
    {
      header: "Installment Amount",
      accessor: "formattedInstallmentAmount",
      sortAccessor: (r) => r.installmentAmount || 0,
    },
    { header: "No. of Installments", accessor: "noOfInstallments" },
    {
      header: "Pending Amount",
      accessor: "formattedPendingAmount",
      sortAccessor: (r) => r.pendingAmount || 0,
    },
    { header: "Pending Installments", accessor: "pendingInstallments" },
    {
      header: "Approval Status",
      accessor: "fundApprovalStatus",
      render: (r) => <StatusCard status={r.fundApprovalStatus} />,
    },
    {
      header: "Payment Status",
      accessor: "paymentStatus",
      render: (r) => <StatusCard status={r.paymentStatus} />,
    },
    {
      header: "Date",
      accessor: "formattedDate",
      sortAccessor: (r) =>
        r.createdAt ? new Date(r.createdAt).getTime() : null,
    },
    {
      header: "Actions",
      key: "actions",
      sortable: false,
      align: "right",
      render: (r) =>
        r.fundApprovalStatus === "Requested" ? (
          <button
            type="button"
            className="hr-btn hr-btn--secondary ef-cancel-btn"
            aria-label={`Cancel emergency fund request of ${inr(r.totalAmount)}`}
            onClick={() => setPendingCancelId(r.emergencyFundId)}
          >
            <Ban size={14} aria-hidden="true" />
            Cancel
          </button>
        ) : (
          <span className="hr-muted">—</span>
        ),
    },
  ];

  return (
    <div className="emergency-funds-page">
      <PageHeader
        breadcrumbs={[
          { label: "Self Service", to: "/ess/my-leave" },
          { label: "Emergency Funds" },
        ]}
        title="Emergency Fund Management"
        subtitle="Submit and track your emergency fund requests."
      />

      <div className="hr-summary-grid">
        <SummaryCard
          tone="warning"
          icon="pending_actions"
          label="Awaiting Review"
          value={loadError ? "–" : stats.pending}
          hint="Requests in review"
          loading={loading}
        />
        <SummaryCard
          tone="success"
          icon="task_alt"
          label="Approved"
          value={loadError ? "–" : stats.approved}
          hint="Approved requests"
          loading={loading}
        />
        <SummaryCard
          tone="danger"
          icon="cancel"
          label="Rejected / Cancelled"
          value={loadError ? "–" : stats.rejected}
          hint="Not accepted"
          loading={loading}
        />
        <SummaryCard
          tone="primary"
          icon="account_balance_wallet"
          label="Total Amount"
          value={loadError ? "–" : inr(stats.totalAmount)}
          hint="Across all requests"
          loading={loading}
        />
      </div>

      <section className="hr-card ef-card" aria-labelledby="ef-form-title">
        <h2 id="ef-form-title">New emergency fund request</h2>
        <p className="ef-card__desc">
          Choose the type, the amount and how many installments you'd like.
        </p>

        <form
          className="ef-form"
          onSubmit={(e) => {
            e.preventDefault();
            handleSubmitRequest();
          }}
        >
          <div className="ef-grid">
            <div className="ef-field">
              <label htmlFor="fundType">Fund type *</label>
              <select
                id="fundType"
                name="fundType"
                className="hr-select__control"
                value={newRequest.fundType}
                onChange={handleInputChange}
                required
              >
                <option value="LOAN">Loan</option>
                <option value="ADVANCE">Advance</option>
              </select>
            </div>

            <div className="ef-field">
              <label htmlFor="totalAmount">Amount *</label>
              <div className="ef-currency">
                <span className="ef-currency__symbol" aria-hidden="true">
                  ₹
                </span>
                <input
                  type="number"
                  id="totalAmount"
                  name="totalAmount"
                  className="hr-input"
                  value={newRequest.totalAmount}
                  onChange={handleInputChange}
                  required
                  min="0"
                  placeholder="e.g., 50000"
                />
              </div>
            </div>

            <div className="ef-field">
              <label htmlFor="noOfInstallments">Number of installments *</label>
              <input
                type="number"
                id="noOfInstallments"
                name="noOfInstallments"
                className="hr-input"
                value={newRequest.noOfInstallments}
                onChange={handleInputChange}
                required
                min="1"
                max="24"
                placeholder="e.g., 12 (max 24)"
              />
            </div>

            <div className="ef-field">
              <span className="ef-label" id="ef-installment-label">
                Installment amount
              </span>
              <output
                className="ef-installment"
                aria-labelledby="ef-installment-label"
              >
                ₹
                {Number(newRequest.installmentAmount).toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </output>
            </div>
          </div>

          {formError && (
            <p className="ef-error" role="alert">
              {formError}
            </p>
          )}

          <div className="ef-actions">
            <button
              type="submit"
              className="hr-btn hr-btn--primary hr-btn--lg"
              disabled={submitting}
            >
              {submitting ? "Submitting…" : "Submit Request"}
            </button>
          </div>
        </form>
      </section>

      {loadError ? (
        <section className="hr-card">
          <ErrorState
            title="Couldn't load your fund requests"
            message="Check your connection and try again."
            onRetry={retryLoad}
          />
        </section>
      ) : (
        <GeneralTable
          columns={columns}
          data={rows}
          loading={loading}
          rowKey={(row, index) => row.emergencyFundId ?? index}
          title="Emergency Funds History"
          emptyStateTitle="No fund requests yet"
          emptyStateSubtitle="Requests you submit will be tracked here."
        />
      )}

      <ConfirmationDialog
        open={Boolean(pendingCancelId)}
        title="Cancel Request"
        message="Are you sure you want to cancel this emergency fund request? This action cannot be undone."
        confirmLabel="Yes, Cancel Request"
        cancelLabel="Keep Request"
        busy={cancelling}
        onConfirm={handleCancelConfirmed}
        onCancel={() => setPendingCancelId(null)}
      />
    </div>
  );
}

export default EmergencyFunds;
